import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useResettablePagination } from '../../hooks/useResettablePagination';
import { Box, Typography, LinearProgress, useTheme } from '@mui/material';
import TableComponent from '../TableComponent';
import { useParams } from 'react-router-dom';
import CustomChip from '../chips/CustomChip';
import { getTestLaunchesForGroup, updateTestLaunchStatus } from '../../utils/api/testlaunch_apis';
import { fetchEnvironmentList } from '../../utils/api/environment_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { renderTableCell } from '../../utils/commonfunctions';
import DEFAULT_ROUTES from '../../constants/routes';
import { useSelector } from 'react-redux';
import { InputBox, Selectbox } from '../FormComponent';
import styles from '../../styles/launchesTable/index.module.scss';
import { formatStatusKey } from '../TestLaunchDetail/statusColorPallete';
const { TestLaunchStatus } = require('../../enums/TestLaunchEnums');
const { LaunchType, LaunchTypeLabel } = require('../../enums/ScheduleEnums');
import { useSnackbar } from '../../utils/useSnackbar';
import ChangeStatus from '../chips/ChangeStatus';
import { getTestLaunchStatusVariant, getStatusIcon } from '../chips/ChipComponent/chipVariants';
import TestGroupLaunchesEmptyPanel from './TestGroupLaunchesEmptyPanel';
import { getLaunchExecutionDurationDisplay } from './launchExecutionDurationDisplay';
import moment from 'moment';

const DEFAULT_FILTERS = {
  search: '',
  status: 'all',
  trigger: 'all',
  launchType: 'all',
  environmentId: 'all',
};

const LaunchesTable = forwardRef(({ groupId, onCreateLaunch, isCreateLaunchDisabled, onViewSchedule }, ref) => {
  const { group_id } = useParams();
  const [launches, setLaunches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [environmentOptions, setEnvironmentOptions] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [paginationModel, setPaginationModel] = useResettablePagination({
    pageSize: 25,
    resetKey: `${filters.search}|${filters.status}|${filters.trigger}|${filters.launchType}|${filters.environmentId}`,
  });
  const { selectedProject } = useSelector((state) => state.projects);
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  const fetchLaunchesData = async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const response = await getTestLaunchesForGroup({
        testGroupId: groupId,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: filters.search,
        status: filters.status,
        trigger: filters.trigger,
        launchType: filters.launchType,
        environmentId: filters.environmentId,
      });

      if (response.status === STATUS.SUCCESS) {
        const res = response.data || {};
        setLaunches(
          (res.rows || []).map((l) => ({
            ...l,
            launchDate: l.createdAt ? l.createdAt : '',
          }))
        );
        setTotal(res.total || 0);
      } else {
        setLaunches([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching launches:', error);
      setLaunches([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: fetchLaunchesData,
  }), [groupId, paginationModel.page, paginationModel.pageSize, filters.search, filters.status, filters.trigger, filters.launchType, filters.environmentId]);

  useEffect(() => {
    let cancelled = false;
    fetchEnvironmentList().then((res) => {
      if (cancelled || res.status !== STATUS.SUCCESS) return;
      const rows = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
      setEnvironmentOptions(
        rows.map((env) => ({
          value: env.id,
          label: env.name || env.id,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchLaunchesData();
  }, [groupId, paginationModel.page, paginationModel.pageSize, filters.search, filters.status, filters.trigger, filters.launchType, filters.environmentId]);

  const handleStatusChange = async (newStatus, testLaunchId) => {
    try {
      const response = await updateTestLaunchStatus({
        launchId: testLaunchId,
        status: newStatus,
      });

      if (response.status === STATUS.SUCCESS) {
        fetchLaunchesData();
        showSnackbar(response.message || `Test Launch status updated to ${formatStatusKey(newStatus)} successfully!`);
      } else {
        showSnackbar(response.error || 'Status update failed', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to update status', 'error');
      console.error('Error updating status:', error);
    }
  };

  const columns = [
    {
      field: 'key',
      headerName: 'Launch ID',
      minWidth: 100,
      maxWidth: 120,
      flex: 1,
      renderCell: (params) => (
        renderTableCell(params.value, 'link', {
          type: 'link',
          to: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}${DEFAULT_ROUTES.TEST_GROUPS}/${group_id}/launch-details/${params.row.id}`,
        })
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <span className={styles['launch-name']} title={params.row.name}>{params.row.name}</span>
      ),
    },
    {
      field: 'launchType',
      headerName: 'Type',
      minWidth: 110,
      maxWidth: 130,
      flex: 0.8,
      renderCell: (params) => {
        const palette = theme.palette.customColor;
        const type = params.value || LaunchType.COMPLETE;
        const label = LaunchTypeLabel[type] || type;
        const isAutomated = type === LaunchType.AUTOMATED;
        const isManual = type === LaunchType.MANUAL;
        return (
          <CustomChip
            label={label}
            color={isAutomated ? palette.blue[50] : isManual ? palette.slate[50] : palette.green[50]}
            textColor={isAutomated ? palette.blue[600] : isManual ? palette.slate[700] : palette.green[600]}
            borderColor={isAutomated ? palette.blue[200] : isManual ? palette.slate[200] : palette.green[200]}
            size="small"
          />
        );
      },
    },
    {
      field: 'isSequential',
      headerName: 'Run Type',
      minWidth: 100,
      maxWidth: 120,
      flex: 0.8,
      renderCell: (params) => {
        const palette = theme.palette.customColor;
        return (
          <CustomChip
            label={params.value ? 'Sequential' : 'Parallel'}
            color={params.value ? palette.blue[50] : palette.green[50]}
            textColor={params.value ? palette.blue[600] : palette.green[600]}
            borderColor={params.value ? palette.blue[200] : palette.green[200]}
            size="small"
          />
        );
      },
    },
    {
      field: 'environmentName',
      headerName: 'Environment',
      minWidth: 110,
      maxWidth: 140,
      flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'triggerType',
      headerName: 'Trigger Type',
      minWidth: 110,
      maxWidth: 130,
      flex: 0.8,
      renderCell: (params) => {
        const palette = theme.palette.customColor;
        const isScheduled = params.value === 'Scheduled';
        return (
          <CustomChip
            label={params.value || 'Manual'}
            color={isScheduled ? palette.blue[50] : palette.slate[50]}
            textColor={isScheduled ? palette.blue[600] : palette.slate[700]}
            borderColor={isScheduled ? palette.blue[200] : palette.slate[200]}
            size="small"
          />
        );
      },
    },
    {
      field: 'launchDate',
      headerName: 'Date & Time',
      minWidth: 120,
      maxWidth: 150,
      flex: 1.2,
      renderCell: (params) => (
        <Box className={styles['date-time-cell']}>
          <Typography variant="body2" className={styles.date}>{moment(params.value).format('YYYY-MM-DD')}</Typography>
          <Typography variant="caption" className={styles.time}>{moment(params.value).local().format('hh:mm A')}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 150,
      maxWidth: 210,
      flex: 1,
      renderCell: (params) => (
        <ChangeStatus
          status={params.value}
          availableStatuses={[TestLaunchStatus.COMPLETED, TestLaunchStatus.ABORTED]}
          getVariant={getTestLaunchStatusVariant}
          getIcon={getStatusIcon}
          formatStatus={formatStatusKey}
          onStatusChange={(newStatus) => handleStatusChange(newStatus, params.row.id)}
          confirmChange={true}
          confirmTitle="Update Status"
          confirmMessage="Are you sure you want to change the Test Launch status? This action cannot be undone."
          size="small"
          showTooltip={false}
        />
      ),
    },
    {
      field: 'results',
      headerName: 'Results',
      minWidth: 120,
      maxWidth: 150,
      flex: 1.2,
      renderCell: (params) => (
        <Box className={styles['results-cell']}>
          <Typography variant="body2" className={styles['results-text']}>
            {params.row.passed}/{params.row.total} ({params.row.passRate}%)
          </Typography>
          <LinearProgress
            variant="determinate"
            value={params.row.passRate}
            className={`${styles['progress-bar']} ${params.row.passRate === 100 ? styles.success : params.row.passRate >= 80 ? styles.warning : styles.error}`}
          />
        </Box>
      ),
    },
    {
      field: 'duration',
      headerName: 'Execution Duration',
      minWidth: 80,
      maxWidth: 100,
      flex: 0.8,
      renderCell: (params) =>
        getLaunchExecutionDurationDisplay(params.row.isVisibleExecutionDuration, params.value),
    },
    {
      field: 'triggeredBy',
      headerName: 'Triggered By',
      minWidth: 120,
      maxWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const scheduleId = params.row.scheduleId;
        const label = params.value || '—';
        if (scheduleId && onViewSchedule) {
          return (
            <button
              type="button"
              className={styles['schedule-name-link']}
              title={label}
              onClick={(e) => {
                e.stopPropagation();
                onViewSchedule({ id: scheduleId, name: params.row.scheduleName || label });
              }}
            >
              {label}
            </button>
          );
        }
        return (
          <Typography variant="body2" className={styles['triggered-by-text']}>
            {label}
          </Typography>
        );
      },
    },
  ];

  const hasDefaultFilters =
    (filters.search ?? '') === ''
    && filters.status === 'all'
    && filters.trigger === 'all'
    && filters.launchType === 'all'
    && filters.environmentId === 'all';

  const isEmptyResult = total === 0 && hasDefaultFilters;
  const isLaunchHistoryEmpty = !loading && isEmptyResult;
  const hideFilterSection = isEmptyResult && (loading || isLaunchHistoryEmpty);

  return (
    <Box className={styles['launches-table-container']}>
      {!hideFilterSection && (
        <Box className={styles['filter-section']}>
          <InputBox
            placeholder="Search launches..."
            value={filters.search || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            className={styles['search-input']}
            name="search"
            label="Search launch"
          />
          <Selectbox
            value={filters.status || 'all'}
            disableClearable={(filters.status || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value || 'all' }))}
            className={styles['status-select']}
            name="status"
            options={[
              { value: 'all', label: 'All Status' },
              { value: TestLaunchStatus.COMPLETED, label: 'Completed' },
              { value: TestLaunchStatus.NEW, label: 'New' },
              { value: TestLaunchStatus.IN_PROGRESS, label: 'In Progress' },
              { value: TestLaunchStatus.ABORTED, label: 'Aborted' },
            ]}
          />
          <Selectbox
            value={filters.launchType || 'all'}
            disableClearable={(filters.launchType || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, launchType: e.target.value || 'all' }))}
            className={styles['status-select']}
            name="launchType"
            options={[
              { value: 'all', label: 'All Types' },
              { value: LaunchType.AUTOMATED, label: LaunchTypeLabel[LaunchType.AUTOMATED] },
              { value: LaunchType.MANUAL, label: LaunchTypeLabel[LaunchType.MANUAL] },
              { value: LaunchType.COMPLETE, label: LaunchTypeLabel[LaunchType.COMPLETE] },
            ]}
          />
          <Selectbox
            value={filters.environmentId || 'all'}
            disableClearable={(filters.environmentId || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, environmentId: e.target.value || 'all' }))}
            className={styles['environment-select']}
            name="environment"
            options={[
              { value: 'all', label: 'All Environments' },
              ...environmentOptions,
            ]}
          />
          <Selectbox
            value={filters.trigger || 'all'}
            disableClearable={(filters.trigger || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, trigger: e.target.value || 'all' }))}
            className={styles['trigger-select']}
            name="trigger"
            options={[
              { value: 'all', label: 'All Triggers' },
              { value: 'manual', label: 'Manual' },
              { value: 'scheduled', label: 'Scheduled' },
            ]}
          />
        </Box>
      )}
      <Box className={`${styles['table-container']}`}>
        <TableComponent
          title="Test Launch History"
          headerActionButtons={[]}
          rows={launches}
          columns={columns}
          isCheckbox={false}
          isRowSelection={false}
          loading={loading}
          isColumnResizable={!loading}
          isPagination={true}
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          handleCustomPaginationPageChange={(event, page) => setPaginationModel({ ...paginationModel, page })}
          handleCustomPaginationPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
          isCustomPagination={true}
          filterMode="client"
          sortingMode="client"
          disableFooter={false}
          customStyles={{
            '& .MuiDataGrid-cell': {
              py: 1,
            },
            '& .selected-row': {
              backgroundColor: theme.palette.customColor.blue[50],
              '&:hover': {
                backgroundColor: theme.palette.customColor.blue[100],
              },
            },
          }}
          className={styles['launches-table']}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 100}
          hideColumns={['name']}
          isData={!isLaunchHistoryEmpty}
          customNoData={(
            <TestGroupLaunchesEmptyPanel
              onCreateLaunch={onCreateLaunch}
              isCreateLaunchDisabled={isCreateLaunchDisabled}
            />
          )}
        />
      </Box>
    </Box>
  );
});

LaunchesTable.displayName = 'LaunchesTable';

export default LaunchesTable;
